import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useT } from "../i18n";
import { Bell, BellOff, BellRing } from "lucide-react";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/**
 * NotificationsToggle — Card in Settings to opt in/out of push notifications.
 *
 * On click:
 *   1. Asks Notification permission.
 *   2. Registers/uses the existing service worker.
 *   3. Calls PushManager.subscribe with the VAPID public key from /api/push/config.
 *   4. POSTs the subscription to /api/push/subscribe (server stores it for the active household).
 */
export default function NotificationsToggle() {
  const { t } = useT();
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");

  const supported = typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;

  const load = async () => {
    if (!supported) return;
    try {
      const r = await api.get("/push/config");
      setConfig(r.data);
      setSubscribed(!!r.data.subscribed);
    } catch (_e) { /* noop */ }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const enable = async () => {
    if (!supported || !config?.enabled) {
      toast.error(t("notif.unsupported"));
      return;
    }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error(t("notif.denied"));
        return;
      }
      const reg = (await navigator.serviceWorker.getRegistration()) || (await navigator.serviceWorker.register("/sw.js"));
      await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(config.vapid_public_key),
        });
      }
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      });
      setSubscribed(true);
      toast.success(t("notif.enabled"));
    } catch (e) {
      toast.error(e?.message || t("common.error"));
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        const json = sub.toJSON();
        try {
          await api.post("/push/unsubscribe", { endpoint: json.endpoint, keys: json.keys || {} });
        } catch (_e) { /* server may already be gone */ }
        try { await sub.unsubscribe(); } catch (_e) { /* noop */ }
      }
      setSubscribed(false);
      toast.success(t("notif.disabled"));
    } catch (e) {
      toast.error(e?.message || t("common.error"));
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/push/test", {});
      const sent = r.data?.sent || 0;
      const total = r.data?.total || 0;
      const reason = r.data?.reason;
      const errs = r.data?.errors || [];
      if (sent > 0) {
        toast.success(t("notif.test_sent", { n: sent }));
      } else if (reason === "no_subscriptions" || total === 0) {
        toast.error(t("notif.test_no_subs"));
      } else if (errs.length > 0) {
        const first = errs[0];
        toast.error(`Push failed (${first.status || "?"}): ${(first.error || "").slice(0, 120)}`);
        // eslint-disable-next-line no-console
        console.error("[Niper] push test errors", errs);
      } else {
        toast.error(t("common.error"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  if (!supported) {
    return (
      <div className="mt-4 rounded-3xl bg-zinc-50 border border-zinc-100 p-5 flex items-center gap-3" data-testid="notif-unsupported">
        <div className="w-11 h-11 rounded-2xl bg-zinc-200 text-zinc-500 flex items-center justify-center"><BellOff className="w-5 h-5" /></div>
        <div className="flex-1 text-sm text-zinc-500">{t("notif.unsupported")}</div>
      </div>
    );
  }

  if (!config?.enabled) {
    return (
      <div className="mt-4 rounded-3xl bg-zinc-50 border border-zinc-100 p-5 flex items-center gap-3" data-testid="notif-disabled-server">
        <div className="w-11 h-11 rounded-2xl bg-zinc-200 text-zinc-500 flex items-center justify-center"><BellOff className="w-5 h-5" /></div>
        <div className="flex-1 text-sm text-zinc-500">{t("notif.server_off")}</div>
      </div>
    );
  }

  return (
    <div className={`mt-4 rounded-3xl p-5 border ${subscribed ? "bg-fresh-green-soft border-fresh-green/30" : "bg-zinc-950 text-white border-zinc-950"}`} data-testid="notif-card">
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${subscribed ? "bg-fresh-green text-black" : "bg-fresh-green text-black"}`}>
          {subscribed ? <BellRing className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-heading text-base font-medium ${subscribed ? "text-fresh-green-text" : "text-white"}`}>{t("notif.title")}</div>
          <div className={`text-xs mt-0.5 ${subscribed ? "text-fresh-green-text/80" : "text-white/70"}`}>
            {subscribed ? t("notif.desc.on") : t("notif.desc.off")}
          </div>
        </div>
      </div>

      {permission === "denied" && (
        <div className="mt-3 text-[11px] text-fresh-red-text bg-fresh-red-soft rounded-lg px-3 py-2" data-testid="notif-denied-msg">
          {t("notif.permission_denied")}
        </div>
      )}

      <div className="mt-4 flex gap-2">
        {subscribed ? (
          <>
            <button
              onClick={sendTest}
              disabled={busy}
              className="flex-1 h-11 rounded-xl bg-white text-zinc-950 font-bold text-sm active:scale-95 border border-zinc-200"
              data-testid="notif-test-btn"
            >
              {t("notif.test_btn")}
            </button>
            <button
              onClick={disable}
              disabled={busy}
              className="px-4 h-11 rounded-xl bg-zinc-100 text-zinc-700 text-sm font-semibold active:scale-95"
              data-testid="notif-disable-btn"
            >
              {t("notif.disable")}
            </button>
          </>
        ) : (
          <button
            onClick={enable}
            disabled={busy || permission === "denied"}
            className="flex-1 h-11 rounded-xl bg-fresh-green text-black font-bold text-sm active:scale-95 disabled:opacity-50"
            data-testid="notif-enable-btn"
          >
            {t("notif.enable_btn")}
          </button>
        )}
      </div>
    </div>
  );
}
